import { LoadbarComponent } from "appworks/components/preloader/loadbar-component";
import { LoadType, PromptContent } from "appworks/components/prompt/prompt-content";
import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { loader } from "appworks/loader/loader";
import { Model } from "appworks/model/model";
import { Contract } from "appworks/utils/contracts/contract";
import { KeyboardCode } from "appworks/utils/keyboard";
import { Timer } from "appworks/utils/timer";
import { DisplayObject, LoaderResource } from "pixi.js";
import { AbstractComponent } from "../abstract-component";

export class PromptComponent extends AbstractComponent {

    public static DEFAULT_TIME = 5000;
    public static SPACE_TO_CONTINUE: boolean = true;
    public static ENTER_TO_CONTINUE: boolean = false;

    private keyListener: EventListenerOrEventListenerObject;

    private KEY_UP_EVENT: string = "keyup";

    public showPrompt(prompt: PromptContent, loadStage: number = null): Contract<void> {

        return new Contract<void>((resolve) => {

            prompt.show().then(() => {
                let raceStages = 1;

                const raceHandler = () => {
                    raceStages--;
                    if (raceStages === 0) {
                        prompt.hide().then(() => resolve(null)).execute();
                    }
                };

                let pointerDown = false;

                const onPointerDown = () => {
                    pointerDown = true;
                };

                const onClickToSkip = (event: any) => {
                    if (pointerDown) {
                        onContinue();
                    }
                };

                prompt.enableContinueButton();

                if (loadStage) {
                    raceStages++;
                    if (prompt.getLoadRaceType() === LoadType.DISABLE_BUTTON) {
                        prompt.disableContinueButton();
                    } else if (prompt.getLoadRaceType() === LoadType.HIDE_BUTTON) {
                        prompt.hideContinueButton();
                    }

                    this.loadStage(loadStage).then(() => {
                        prompt.enableContinueButton();

                        if (prompt.getClickToSkip()) {
                            canvasManager.rootNode.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                            canvasManager.rootNode.addEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);
                        }

                        raceHandler();
                    });
                } else if (prompt.getClickToSkip()) {
                    canvasManager.rootNode.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                    canvasManager.rootNode.addEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);
                }

                if (this.keyListener) {
                    document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
                }
                this.keyListener = (event: any) => {
                    const spaceKeyPressed = (PromptComponent.SPACE_TO_CONTINUE && event.key === KeyboardCode.SPACE);
                    const enterKeyPressed = (PromptComponent.ENTER_TO_CONTINUE && event.key === KeyboardCode.ENTER);
                    const skipKeyPressed = spaceKeyPressed || enterKeyPressed;
                    if (Model.read().settings.spaceToSpin && skipKeyPressed) {
                        onContinue();
                    }
                };

                const onContinue = () => {
                    document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
                    canvasManager.rootNode.removeEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                    canvasManager.rootNode.removeEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);

                    if (prompt.getLoadRaceType() === LoadType.LOADING_SCENE && Layers.get("Prompts").hasScene("loading")) {
                        Layers.get("Prompts").setScene("loading").then(() => raceHandler());
                    } else {
                        raceHandler();
                    }
                };

                if (prompt.hasContinueButton()) {
                    document.addEventListener(this.KEY_UP_EVENT, this.keyListener);
                    prompt.onHide.add(() => this.removeKeyListener());
                    prompt.addContinueListener(() => {
                        onContinue();
                    });
                } else {
                    Timer.setTimeout(() => {
                        onContinue();
                    }, prompt.timeOutTime);
                }
            });
        });
    }

    private removeKeyListener() {
        document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
    }

    private loadStage(stageId: number): Contract<void> {

        const bar = Layers.get("Prompts").getSprite("loadbar");

        const loadBar = new LoadbarComponent(loader.getStageFiles(stageId), bar);

        let loadSprite: DisplayObject = Layers.get("Prompts").getSprite("loading_sprite");
        if (!loadSprite) {
            const loadButton = Layers.get("Prompts").getButton("loading_sprite");
            if (loadButton) {
                loadSprite = loadButton;
            }
        }

        loadBar.addLoadingText(Layers.get("Prompts").getText("loading_label"), Layers.get("Prompts").getText("loading_percentage"), loadSprite);

        return new Contract<void>((resolve) => {
            loader.loadStage(stageId, (stage: number, progress: number, resource: LoaderResource) => {
                loadBar.fileLoaded(resource);
            }).then(() => loadBar.loadComplete().then(() => resolve(null)));
        });
    }
}
